import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { InstitutionsService } from '@stockcare-services/institution-service/institution.service';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { PhysicalLocationsService } from '@stockcare-services/physical-locations-service/physical-locations.service';
import { StockLocationsService } from '@stockcare-services/stock-locations-service/stock-locations.service';
import { IDevice } from '@stockcare-models/IDevice';
import { IInstitution } from '@stockcare-models/IInstitution';
import { IPhysicalLocation } from '@stockcare-models/IPhysical-location';
import { IStockLocation } from '@stockcare-models/IStock-location';
import { ThemeService } from '@stockcare-services/theme/theme.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IKeyValue } from '@stockcare-models/IKeyValue';
import { AuthenticationStateService } from '@stockcare-services/authentication/authentication-state.service';
import { IPhysicalLocationTheatreRoom } from '@stockcare-models/IPhysicalLocationTheatreRoom';
import { DeviceType } from '@stockcare-models/DeviceType';
import { Settings } from '@stockcare-helpers/settings';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';

@Component({
  standalone: false,
  selector: 'app-link-device',
  templateUrl: './link-device.component.html',
  styleUrls: ['./link-device.component.scss'],
})
export class LinkDeviceComponent implements OnInit, OnDestroy {
  enableLinkExistingDevice: boolean = false;

  institutions: IInstitution[];
  devices: IDevice[];
  filteredDevices: IDevice[];
  stockLocations: IStockLocation[];
  physicalLocations: IPhysicalLocation[];
  deviceTypes: IKeyValue[];
  addDeviceForm: UntypedFormGroup;
  logo: string;
  theatreRooms: IPhysicalLocationTheatreRoom[];
  private subscriptions = new Subscription();

  constructor(
    private settingService: SettingsService,
    private deviceService: DeviceService,
    private physicalLocationsService: PhysicalLocationsService,
    private stockLocationService: StockLocationsService,
    private institutionService: InstitutionsService,
    private router: Router,
    private notify: NotificationService,
    private zone: NgZone,
    themeService: ThemeService,
    private authenticationStateService: AuthenticationStateService
  ) {
    if (themeService.getTheme() != null) {
      this.logo = '../../../assets/img/' + themeService.getTheme().logo;
    } else {
      this.logo = '../../../assets/img/stockcare-default-logo.png';
    }
  }

  get controls() {
    return this.addDeviceForm.controls;
  }

  ngOnInit() {
    this.enableLinkExistingDevice = this.settingService.getBooleanSetting(Settings.ENABLE_LINK_EXISTING_DEVICE);

    this.deviceService.getDeviceTypes().subscribe((x) => {
      this.deviceTypes = x.sort(this.compareDeviceTypes);
    });
    this.checkIfDeviceisAlreadyLinked();
  }

  private checkIfDeviceisAlreadyLinked() {
    if (this.deviceService.isDeviceLinked()) {
      this.navigate();
    } else {
      this.buildForm();
      this.buildDropdowns();
    }
  }

  private buildForm() {
    this.addDeviceForm = new UntypedFormGroup({
      name: new UntypedFormControl(null),
      deviceType: new UntypedFormControl(null, Validators.required),
      institution: new UntypedFormControl(null, Validators.required),
      stockLocation: new UntypedFormControl(null, Validators.required),
      physicalLocation: new UntypedFormControl(null, Validators.required),
      filteredDevice: new UntypedFormControl(null),
      physicalLocationTheatreRoom: new UntypedFormControl(null),
    });
  }

  private buildDropdowns() {
    this.subscriptions.add(
      this.institutionService.get().subscribe((institutions) => {
        this.institutions = institutions.sort(this.compareInstitutions);
      })
    );
  }

  onInstitutionChanged() {
    const institution = this.addDeviceForm.controls.institution.value;
    this.stockLocationService.getByInstitution(institution.id).subscribe((stockLocations) => {
      this.stockLocations = stockLocations.sort(this.compareStockLocations);
    });
    this.physicalLocationsService.getByInstitution(institution.id).subscribe((physicalLocations) => {
      this.physicalLocations = physicalLocations.sort(this.comparePhysicalLocations);
    });
    this.subscriptions.add(
      this.deviceService.getAll().subscribe((devices) => {
        this.devices = devices.filter((x) => x.institutionId === institution.id);
      })
    );
    this.clearFormControls();
  }

  filter() {
    const institution = this.addDeviceForm.controls.institution.value;
    const stockLocation = this.addDeviceForm.controls.stockLocation.value;
    const physicalLocation = this.addDeviceForm.controls.physicalLocation.value;

    if (institution && stockLocation && physicalLocation) {
      this.filteredDevices = this.devices.filter(
        (x) =>
          x.institutionId === institution.id &&
          x.stockLocationId === stockLocation.id &&
          x.physicalLocationId === physicalLocation.id &&
          x.isRemoved === false
      );
      console.log(this.filteredDevices);
    } else {
      this.filteredDevices = [];
    }
  }

  isDeviceActive(lastHeartbeatReceived: Date) {
    let lastHeartbeat = new Date(lastHeartbeatReceived.toString()).getTime();
    let today = new Date().getTime();
    const minutesBetween = (today - lastHeartbeat) / 60000

    if (minutesBetween <= 30) {
      return true;
    } else {
      return false;
    }
  }

  clearFormControls() {
    this.addDeviceForm.controls.stockLocation.setValue(null);
    this.addDeviceForm.controls.physicalLocation.setValue(null);
    this.addDeviceForm.controls.filteredDevice.setValue(null);
  }

  linkDevice() {
    if (!this.validate()) {
      return;
    }

    const existingDevice = this.existingDevice();
    if (this.enableLinkExistingDevice && existingDevice) {
      this.deviceService.saveDeviceToLocalStorage(existingDevice);
      this.deviceLinked(existingDevice);
    } else {
      const deviceName = this.controls.name.value;
      if (deviceName === null || deviceName === undefined || deviceName === '') {
        this.notify.warning('Please enter a device name');
        return;
      }

      const device = this.createDevice();
      this.deviceService.create(device).subscribe(
        () => {
          this.deviceLinked(device);
        },
        (error) => {
          this.notify.error(error.error);
        }
      );
    }
  }

  private createDevice(): IDevice {
    return {
      name: this.controls.name.value,
      stockLocationId: this.controls.stockLocation.value.id,
      physicalLocationId: this.controls.physicalLocation.value.id,
      institutionId: this.controls.institution.value.id,
      deviceType: this.addDeviceForm.controls.deviceType.value,
      physicalLocationTheatreRoomId: this.controls.physicalLocationTheatreRoom.value,
    } as IDevice;
  }

  private existingDevice(): IDevice {
    return this.controls.filteredDevice.value as IDevice;
  }

  private deviceLinked(device: IDevice) {
    this.zone.run(async () => {
      this.authenticationStateService.updateLoggedInState(true);
      this.notify.success('Device successfully linked');
      this.navigate();
    });
  }

  private validate(): boolean {
    if (!this.addDeviceForm.valid) {
      this.touchAllControls();
      return false;
    }
    return true;
  }

  private touchAllControls() {
    for (const i of Object.keys(this.addDeviceForm.controls)) {
      this.addDeviceForm.controls[i].markAsTouched();
    }
  }

  private navigate() {
    if (this.deviceService.isWhiteboardDevice()) {
      this.navigateToWhiteBoard();
      return;
    }

    if (this.deviceService.isTheaterDevice()) {
      this.navigateToTheater();
      return;
    }

    this.navigateToHome();
  }

  private navigateToHome() {
    this.router.navigate(['app/patients']);
    return;
  }

  private navigateToTheater() {
    this.router.navigate(['app/theatre']);
    return;
  }

  private navigateToWhiteBoard() {
    this.router.navigate(['app/whiteboard']);
    return;
  }

  buildDeviceRooms() {
    this.theatreRooms = null;

    if (!this.controls.physicalLocation.value || this.controls.deviceType.value !== DeviceType.TheatreOperatingRoom) {
      this.controls.physicalLocationTheatreRoom.setValidators(null);
      this.controls.physicalLocationTheatreRoom.updateValueAndValidity();
      return;
    }

    const physicalLocation = this.physicalLocations.find((x) => x.id === this.controls.physicalLocation.value.id);

    this.theatreRooms = physicalLocation.physicalLocationTheatreRooms;

    this.controls.physicalLocationTheatreRoom.setValidators([Validators.required]);
    this.controls.physicalLocationTheatreRoom.updateValueAndValidity();
  }

  private compareInstitutions(x: IInstitution, y: IInstitution) {
    if (x.name > y.name) {
      return 1;
    } else {
      return -1;
    }
  }

  private comparePhysicalLocations(x: IPhysicalLocation, y: IPhysicalLocation) {
    if (x.name > y.name) {
      return 1;
    } else {
      return -1;
    }
  }

  private compareStockLocations(x: IStockLocation, y: IStockLocation) {
    if (x.name > y.name) {
      return 1;
    } else {
      return -1;
    }
  }

  private compareDeviceTypes(x: IKeyValue, y: IKeyValue) {
    if (x.value > y.value) {
      return 1;
    } else {
      return -1;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
